import { useState } from 'react';
import {
  Text, Button, Spinner, Field, Dropdown, Option,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  tokens,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../../i18n';
import type { TelegramGroup } from '../../api/telegram-groups.api';
import {
  setGroupProposito, SUPERVISION_ROLES,
  type GroupAlcance, type GroupModoCaptura, type GroupPropositoInput, type GroupProposito, type SupervisionRole,
} from '../../api/supervision.api';

/**
 * Propósito del grupo (captura o supervisión), modo de captura, alcance y rol de supervisión.
 * Los valores que se envían son los del enum; las etiquetas son solo texto en pantalla.
 * Se monta con `key` = chatId para que cada grupo empiece con sus valores.
 */
export function TelegramGroupPurposeDialog({ group, onClose }: { group: TelegramGroup | null; onClose: () => void }) {
  const t  = useT();
  const qc = useQueryClient();
  const [proposito, setProposito] = useState<GroupProposito>(group?.proposito ?? 'CAPTURA');
  const [modo, setModo] = useState<GroupModoCaptura>(group?.modoCaptura ?? 'TODO');
  const [alcance, setAlcance] = useState<GroupAlcance>(group?.alcance ?? 'ROL');
  const [rol, setRol] = useState<SupervisionRole | ''>((group?.rol as SupervisionRole) ?? '');
  const [error, setError] = useState('');
  const [rolError, setRolError] = useState(false);

  const roleLabel = (r: string) => (t.settings.roles as Record<string, string>)[r] ?? r;

  const mut = useMutation({
    mutationFn: () => {
      if (!group) throw new Error('sin grupo');
      const body: GroupPropositoInput = proposito === 'CAPTURA'
        ? { proposito, modoCaptura: modo }
        : alcance === 'ROL'
          ? { proposito, alcance, rol: rol as SupervisionRole }
          : { proposito, alcance };
      return setGroupProposito(group.chatId, body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['telegram-groups'] }); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? t.telegramGroupsAdmin.purposeSaveError),
  });

  const handleSave = () => {
    setError('');
    const missingRol = proposito === 'SUPERVISION' && alcance === 'ROL' && !rol;
    setRolError(missingRol);
    if (missingRol) return;
    mut.mutate();
  };

  return (
    <Dialog open={!!group} onOpenChange={(_, d) => { if (!d.open && !mut.isPending) onClose(); }}>
      <DialogSurface style={{ maxWidth: '460px' }}>
        <DialogBody>
          <DialogTitle>{t.telegramGroupsAdmin.purposeDialogTitle.replace('{name}', group?.label ?? '')}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.telegramGroupsAdmin.purposeDialogBody}</Text>
              <Field label={t.telegramGroupsAdmin.purposeLabel}>
                <Dropdown
                  aria-label={t.telegramGroupsAdmin.purposeLabel}
                  value={proposito === 'CAPTURA' ? t.telegramGroupsAdmin.purposeCapture : t.telegramGroupsAdmin.purposeSupervision}
                  selectedOptions={[proposito]}
                  disabled={mut.isPending}
                  onOptionSelect={(_, d) => setProposito((d.optionValue as GroupProposito) ?? 'CAPTURA')}
                >
                  <Option value="CAPTURA" text={t.telegramGroupsAdmin.purposeCapture}>{t.telegramGroupsAdmin.purposeCapture}</Option>
                  <Option value="SUPERVISION" text={t.telegramGroupsAdmin.purposeSupervision}>{t.telegramGroupsAdmin.purposeSupervision}</Option>
                </Dropdown>
              </Field>

              {proposito === 'CAPTURA' && (
                <Field label={t.telegramGroupsAdmin.modeLabel} hint={t.telegramGroupsAdmin.modeHint}>
                  <Dropdown
                    aria-label={t.telegramGroupsAdmin.modeLabel}
                    value={modo === 'TODO' ? t.telegramGroupsAdmin.modeAll : t.telegramGroupsAdmin.modeMention}
                    selectedOptions={[modo]}
                    disabled={mut.isPending}
                    onOptionSelect={(_, d) => setModo((d.optionValue as GroupModoCaptura) ?? 'TODO')}
                  >
                    <Option value="TODO" text={t.telegramGroupsAdmin.modeAll}>{t.telegramGroupsAdmin.modeAll}</Option>
                    <Option value="SOLO_MENCION" text={t.telegramGroupsAdmin.modeMention}>{t.telegramGroupsAdmin.modeMention}</Option>
                  </Dropdown>
                </Field>
              )}

              {proposito === 'SUPERVISION' && (
                <>
                  <Field label={t.telegramGroupsAdmin.scopeLabel}>
                    <Dropdown
                      aria-label={t.telegramGroupsAdmin.scopeLabel}
                      value={alcance === 'ROL' ? t.telegramGroupsAdmin.scopeRole : t.telegramGroupsAdmin.scopeRancho}
                      selectedOptions={[alcance]}
                      disabled={mut.isPending}
                      onOptionSelect={(_, d) => setAlcance((d.optionValue as GroupAlcance) ?? 'ROL')}
                    >
                      <Option value="ROL" text={t.telegramGroupsAdmin.scopeRole}>{t.telegramGroupsAdmin.scopeRole}</Option>
                      <Option value="RANCHO" text={t.telegramGroupsAdmin.scopeRancho}>{t.telegramGroupsAdmin.scopeRancho}</Option>
                    </Dropdown>
                  </Field>
                  {alcance === 'ROL' && (
                    <Field
                      label={t.telegramGroupsAdmin.roleLabel}
                      validationState={rolError ? 'error' : 'none'}
                      validationMessage={rolError ? t.telegramGroupsAdmin.roleRequired : undefined}
                    >
                      <Dropdown
                        aria-label={t.telegramGroupsAdmin.roleLabel}
                        placeholder={t.telegramGroupsAdmin.rolePlaceholder}
                        value={rol ? roleLabel(rol) : ''}
                        selectedOptions={rol ? [rol] : []}
                        disabled={mut.isPending}
                        onOptionSelect={(_, d) => { setRol((d.optionValue as SupervisionRole) ?? ''); setRolError(false); }}
                      >
                        {SUPERVISION_ROLES.map(r => <Option key={r} value={r} text={roleLabel(r)}>{roleLabel(r)}</Option>)}
                      </Dropdown>
                    </Field>
                  )}
                </>
              )}

              {error && <Text role="alert" style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={mut.isPending}>{t.telegramGroupsAdmin.cancel}</Button>
            <Button appearance="primary" onClick={handleSave} disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}>
              {t.common.save}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
